import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search, X, Layers, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLayers } from '../../hooks/useLayers';
import { Layer } from '../../types/api';
import { SubMenuGroup } from '../../store/layer.store';
import { LayerItem } from './LayerItem';
import { WorkspacePanel } from './WorkspacePanel';
import { MyMenusPanel } from './groups/MyMenusPanel';
import { platformRef, useOnlyInMyMenus } from './groups/useLayerGroups';
import { cn } from '../../utils/cn';

interface LayerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

export const LayerPanel: React.FC<LayerPanelProps> = ({ isOpen, onClose, onToggle }) => {
  const { t } = useTranslation();
  const {
    layers,
    borders,
    thematics,
    userOverlays,
    pois,
    confLayers,
    thematicGroups,
    poiGroups,
    visibility,
    isLoading,
    error,
    toggleLayerVisibility,
    toggleLabelVisibility,
    hasLabel,
    isLabelVisible,
    isFavorite,
    toggleFavorite,
  } = useLayers();

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['borders']),
  );
  const [searchQuery, setSearchQuery] = useState('');
  // Layers I put in one of my menus "only there" leave their usual place in my tree.
  const onlyInMyMenus = useOnlyInMyMenus();

  const toggleSection = (id: string) => {
    const next = new Set(expandedSections);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedSections(next);
  };

  const filterLayers = (all: Layer[]) => {
    const list = onlyInMyMenus.platform.size ? all.filter((l) => !onlyInMyMenus.platform.has(platformRef(l))) : all;
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (l) =>
        l.displayName?.toLowerCase().includes(q) ||
        l.layerName?.toLowerCase().includes(q),
    );
  };

  const totalLayers = layers.length;

  /** Render a flat category (borders, user layers, config) */
  const renderCategory = (id: string, label: string, categoryLayers: Layer[]) => {
    const filtered = filterLayers(categoryLayers);
    const isExpanded = expandedSections.has(id);

    return (
      <div key={id} className="border-b border-gn-light">
        <button
          onClick={() => toggleSection(id)}
          className={cn(
            'flex items-center justify-between w-full px-3 py-2 rounded',
            'hover:bg-gn-tertiary transition-colors text-xs font-medium',
            'text-gn-primary hover:text-gn-primary',
          )}
        >
          <div className="flex items-center gap-2">
            <span>{label}</span>
            {categoryLayers.length > 0 && (
              <span className="text-[10px] text-gn-muted">({categoryLayers.length})</span>
            )}
          </div>
          <ChevronDown
            size={14}
            className={cn('transition-transform text-gn-secondary', isExpanded && 'rotate-180')}
          />
        </button>
        {isExpanded && (
          <div className="px-2 pb-2 space-y-0.5">
            {filtered.length > 0 ? (
              filtered.map((layer) => (
                <LayerItem
                  key={layer.id}
                  layer={layer}
                  visible={!!visibility[layer.id]}
                  isFavorite={isFavorite(layer.id)}
                  hasLabel={hasLabel(layer.id)}
                  labelVisible={isLabelVisible(layer.id)}
                  onVisibilityToggle={toggleLayerVisibility}
                  onFavoriteToggle={toggleFavorite}
                  onLabelToggle={toggleLabelVisibility}
                />
              ))
            ) : (
              <p className="text-[10px] text-gn-muted px-2 py-1 italic">
                {searchQuery ? t('layers.no_matching') : t('layers.no_layers')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  /** Render a grouped category (thematics, POIs) with subMenu groups */
  const renderGroupedCategory = (id: string, label: string, groups: SubMenuGroup[], allCategoryLayers: Layer[]) => {
    const isExpanded = expandedSections.has(id);

    return (
      <div key={id} className="border-b border-gn-light">
        <button
          onClick={() => toggleSection(id)}
          className={cn(
            'flex items-center justify-between w-full px-3 py-2 rounded',
            'hover:bg-gn-tertiary transition-colors text-xs font-medium',
            'text-gn-primary hover:text-gn-primary',
          )}
        >
          <div className="flex items-center gap-2">
            <span>{label}</span>
            {allCategoryLayers.length > 0 && (
              <span className="text-[10px] text-gn-muted">({allCategoryLayers.length})</span>
            )}
          </div>
          <ChevronDown
            size={14}
            className={cn('transition-transform text-gn-secondary', isExpanded && 'rotate-180')}
          />
        </button>
        {isExpanded && (
          <div className="px-2 pb-2">
            {groups.length > 0 ? (
              groups.map((group) => {
                const filtered = filterLayers(group.layers);
                if (filtered.length === 0 && searchQuery) return null;
                return (
                  <div key={group.subMenuId} className="mb-1">
                    {groups.length > 1 && (
                      <div className="text-[10px] text-gn-secondary font-medium px-2 pt-1.5 pb-0.5 uppercase tracking-wider">
                        {group.subMenuName}
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {filtered.map((layer) => (
                        <LayerItem
                          key={layer.id}
                          layer={layer}
                          visible={!!visibility[layer.id]}
                          isFavorite={isFavorite(layer.id)}
                          hasLabel={hasLabel(layer.id)}
                          labelVisible={isLabelVisible(layer.id)}
                          onVisibilityToggle={toggleLayerVisibility}
                          onFavoriteToggle={toggleFavorite}
                          onLabelToggle={toggleLabelVisibility}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-[10px] text-gn-muted px-2 py-1 italic">
                {searchQuery ? t('layers.no_matching') : t('layers.no_layers')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Overlay backdrop — mobile only */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 lg:hidden bg-black/30"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Toggle Button — top-left with layers icon + arrow */}
      <button
        onClick={onToggle}
        className={cn(
          'fixed top-3 z-50 flex items-center gap-1',
          'h-9 px-2.5 rounded-lg',
          'bg-gn-surface backdrop-blur-sm shadow-lg border border-gn-light',
          'text-gn-muted hover:bg-gn-tertiary hover:text-gn-primary',
          'transition-all duration-300 ease-out',
          'focus:outline-none focus:ring-2 focus:ring-gray-400',
          isOpen ? 'left-[18.5rem] lg:left-[20.5rem]' : 'left-3',
        )}
        title={isOpen ? t('layers.hide_panel') : t('layers.show_panel')}
      >
        <Layers size={18} />
        {isOpen
          ? <ChevronLeft size={14} className="text-gn-secondary" />
          : <ChevronRight size={14} className="text-gn-secondary" />
        }
      </button>

      {/* Side Panel */}
      <div
        className={cn(
          'fixed top-0 bottom-0 left-0 z-40',
          'w-72 lg:w-80',
          'bg-gn-primary backdrop-blur-sm border-r border-gn',
          'flex flex-col',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        role="complementary"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gn-light">
          <div className="flex items-center gap-2">
            <Layers size={14} className="text-emerald-400" />
            <h2 className="text-sm font-semibold text-gn-primary">{t('map.layers')}</h2>
            {totalLayers > 0 && (
              <span className="text-[10px] text-gn-secondary bg-gn-secondary rounded px-1.5 py-0.5">
                {totalLayers}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gn-secondary transition-colors">
            <X size={16} className="text-gn-secondary" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-gn-light">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gn-secondary pointer-events-none" />
            <input
              type="text"
              placeholder={t('layers.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-1.5 bg-gn-secondary border border-gn rounded text-xs text-gn-primary placeholder-gn-muted focus:outline-none focus:border-gn focus:ring-1 focus:ring-gn-blue"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-gn-blue mr-2" />
              <span className="text-xs text-gn-secondary">{t('layers.loading')}</span>
            </div>
          )}

          {error && !isLoading && (
            <div className="px-4 py-3 mx-3 mt-3 bg-gn-danger-light border border-gn rounded">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="text-gn-danger shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gn-danger">{error}</p>
                  <p className="text-[10px] text-gn-danger mt-1">
                    {t('layers.load_error_hint')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && (
            <div className="py-1">
              <MyMenusPanel platformLayers={layers} visibility={visibility} onTogglePlatform={toggleLayerVisibility} />
              <WorkspacePanel />
              {renderCategory('borders', t('layers.admin_layers'), borders)}
              {renderGroupedCategory('thematics', t('layers.thematic_maps'), thematicGroups, thematics)}
              {renderGroupedCategory('pois', t('layers.pois'), poiGroups, pois)}
              {renderCategory('userLayers', t('layers.user_layers'), userOverlays)}
              {renderCategory('drawing', t('layers.drawing'), confLayers)}
            </div>
          )}

          {!isLoading && !error && totalLayers === 0 && (
            <div className="px-4 py-8 text-center">
              <Layers size={24} className="text-gn-secondary mx-auto mb-2" />
              <p className="text-xs text-gn-secondary">{t('layers.no_layers')}</p>
              <p className="text-[10px] text-gn-muted mt-1">
                {t('layers.none_hint')}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
